import { WorkUnitGridRow, WorkUnitRowData } from './WorkUnitTable'
import type {
  UserRetrieveResponse,
  WorklistsListResponse,
  ProvidersWorkUnitsPartialUpdateResponse,
} from '@fireflyhealth/api'
import {
  AssigneeGroupListItem,
  getAssigneeGroupById,
  getAssigneeGroupName,
} from '~/entities/assignee-group'
import type { ComponentProps } from 'react'
import { Provider } from '~/entities/provider'
import { addDays, endOfDay, format, parseISO, subDays } from 'date-fns'
import { ISO_DATE_FORMAT } from '~/shared/lib/date'
import { type CaseWorkUnit, type Tag } from '~/entities/work-unit'
import { CaseCategory } from '~/entities/case'
import {
  getGridStringOperators,
  GridFilterInputDate,
  GridFilterItem,
  GridFilterOperator,
} from '@mui/x-data-grid'
import { personRowName } from '~/entities/person'
interface WorklistRowBuilderConstructor {
  worklist: WorklistsListResponse[number]
  items: CaseWorkUnit[]
  providers: Provider[] | undefined
  assigneeGroups: AssigneeGroupListItem[] | undefined
  getUserById: (arg0: Provider[], arg1: string) => Provider | UserRetrieveResponse | undefined
  caseCategoriesById: Record<string, CaseCategory | undefined>
}

interface WorklistEditorField {
  displayName: string
  fieldName: string
  id?: number
}

/**
 * Worklists can have rows of 2 types depending on the source data: Worklist Items or Work Units.
 * Use createRows() to build the appropriate type
 */
export class WorklistRowBuilder {
  constructorArgs: WorklistRowBuilderConstructor

  constructor(args: WorklistRowBuilderConstructor) {
    this.constructorArgs = args
  }

  createRows(): WorkUnitGridRow[] {
    const rows: WorkUnitGridRow[] = []
    const { items } = this.constructorArgs

    for (const item of items) {
      const row = this._createWorkUnitRow(item)

      row.data = { ...row }

      rows.push(row)
    }

    return rows
  }

  _createWorkUnitRow = (item: CaseWorkUnit): WorkUnitGridRow => {
    const partialRow = this._createPartialRow(item)
    const { providers, getUserById, caseCategoriesById, worklist } = this.constructorArgs
    const careTeam = providers
      ? (item.content_object.person_meta.care_team || [])
          .map(providerId => {
            const user: Provider = getUserById(providers, providerId.toString()) as Provider
            if (user) {
              return user.first_name
            }
          })
          .join(', ')
      : null
    const person = personRowName(item.content_object.person_meta)
    const createdAt = format(parseISO(item.content_object.created_at), ISO_DATE_FORMAT)
    const dueDate = item.due_date ? format(parseISO(item.due_date), ISO_DATE_FORMAT) : null
    const caseTags = item.content_object.case_tags
    const tags: Tag[] = []
    if (caseTags != null) {
      for (const caseTag of caseTags) {
        tags.push(caseTag.tag)
      }
    }

    const row: WorkUnitGridRow = {
      ...partialRow,
      worklist: worklist,
      ownerGroup: item.owner_group ? item.owner_group.toString() : undefined,
      pk: item.object_id.toString(),
      links: item.case_url ? { caseUrl: item.case_url } : undefined,
      description: item.content_object.description ?? null,
      notes: item.content_object.notes ?? null,
      patientId: item.person_user,
      status: item.content_object.status ?? '',
      createdAt: createdAt,
      careTeam: careTeam,
      podNames: item.content_object.person_meta.pods
        ? item.content_object.person_meta.pods.map(pod => pod.name).join(', ')
        : null,
      elationUrl: item.content_object.person_meta.elation_url,
      person: person,
      dueDate: dueDate,
      contentType: 'case',
      category:
        item.content_object.category != null
          ? (caseCategoriesById[item.content_object.category]?.title ?? null)
          : null,
      actions: item.content_object.actions,
      tags: tags,
      statusCategory: item.status_category,
    }

    return row
  }

  /**
   * Basic row data required by Worklist rows of all types
   */
  _createPartialRow = (item: CaseWorkUnit): Partial<WorkUnitGridRow> => {
    const { worklist, providers, assigneeGroups } = this.constructorArgs

    const partialRow: Partial<WorkUnitGridRow> = {
      id: item.object_id,
      worklist: worklist,
      links: undefined,
    }

    partialRow.ownerGroup = item.owner_group ? item.owner_group.toString() : undefined

    let assignedToAssigneeGroup =
      providers && item.owner_group
        ? getAssigneeGroupById(providers, Number(item.owner_group))
        : null
    if (!assignedToAssigneeGroup && item.owner_group) {
      assignedToAssigneeGroup = assigneeGroups?.find(group => group.id == Number(item.owner_group))
    }
    partialRow.assignedToName = assignedToAssigneeGroup
      ? getAssigneeGroupName(assignedToAssigneeGroup)
      : ''

    return partialRow
  }
}

const worklistEditorFields: {
  prop: keyof WorkUnitRowData
  headerName: string
}[] = [
  { prop: 'category', headerName: 'Case Category' },
  { prop: 'description', headerName: 'Description' },
  { prop: 'notes', headerName: 'Notes' },
  { prop: 'createdAt', headerName: 'Case Created' },
  { prop: 'careTeam', headerName: 'Care Team' },
]

export const createWorklistEditorFields = (): WorklistEditorField[] =>
  worklistEditorFields.map(field => ({
    displayName: field.headerName,
    fieldName: field.prop,
  }))
/**
 * buildApplyFilterFn
 * @param filterItem: take seletected filter
 * @param compareFn: take cmpare function
 * @returns filter out items based on compare function
 */
function buildApplyFilterFn(
  filterItem: GridFilterItem,
  compareFn: (value1: number, value2: number) => boolean,
): ((value: unknown) => boolean) | null {
  if (!filterItem.value) {
    return null
  }
  return (value: unknown) => {
    if (value == null) {
      return false
    }
    const a = new Date(value as string | number | Date).getTime()
    const b = new Date(filterItem.value as string | number | Date).getTime()
    return compareFn(a, b)
  }
}

// Cell values on this column are preformatted `yyyy-MM-dd` strings, not Date objects.
// A missing value yields an invalid date, which compares false against every boundary
// below, so a row with no due date matches none of these filters.
const asDate = (value: unknown) => (typeof value === 'string' ? parseISO(value) : new Date(NaN))

export const worklistCareTeamFilterOperators: GridFilterOperator[] =
  getGridStringOperators().filter(operator => operator.value === 'contains')
// v8 added a 4th generic to GridFilterOperator for the input component's props, and
// `InputComponentProps` is typed against it. `type` lives on GridFilterInputDate rather
// than the base input props, so name that component's props here; deriving them from the
// component avoids depending on a type the package does not re-export.
export const worklistNextActionDateFilterOperators: GridFilterOperator<
  Date,
  unknown,
  unknown,
  ComponentProps<typeof GridFilterInputDate>
>[] = [
  {
    value: 'is',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      return buildApplyFilterFn(filterItem, (value1, value2) => value1 === value2)
    },
    InputComponent: GridFilterInputDate,
    InputComponentProps: {
      type: 'date',
    },
  },
  {
    value: 'after',
    getApplyFilterFn: filterItem => {
      return buildApplyFilterFn(filterItem, (value1, value2) => value1 > value2)
    },
    InputComponent: GridFilterInputDate,
    InputComponentProps: {
      type: 'date',
    },
  },
  {
    value: 'onOrAfter',
    getApplyFilterFn: filterItem => {
      return buildApplyFilterFn(filterItem, (value1, value2) => value1 >= value2)
    },
    InputComponent: GridFilterInputDate,
    InputComponentProps: {
      type: 'date',
    },
  },
  {
    value: 'before',
    getApplyFilterFn: filterItem => {
      return buildApplyFilterFn(filterItem, (value1, value2) => value1 < value2)
    },
    InputComponent: GridFilterInputDate,
    InputComponentProps: {
      type: 'date',
    },
  },
  {
    value: 'onOrBefore',
    getApplyFilterFn: filterItem => {
      return buildApplyFilterFn(filterItem, (value1, value2) => value1 <= value2)
    },
    InputComponent: GridFilterInputDate,
    InputComponentProps: {
      type: 'date',
    },
  },
  {
    value: 'isEmpty',
    getApplyFilterFn: () => {
      return value => {
        return value == null
      }
    },
  },
  {
    value: 'isNotEmpty',
    getApplyFilterFn: () => {
      return value => {
        return value != null
      }
    },
  },
  {
    label: 'is today',
    value: 'today',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.operator) {
        return null
      }

      return (value): boolean => {
        return asDate(value) <= endOfDay(new Date())
      }
    },
  },
  {
    label: 'is overdue',
    value: 'overdue',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.operator) {
        return null
      }

      return (value): boolean => {
        return asDate(value) < endOfDay(subDays(new Date(), 1))
      }
    },
  },
  {
    label: 'is next 7 days',
    value: 'next_7_days',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.operator) {
        return null
      }

      return (value): boolean => {
        return (
          asDate(value) >= endOfDay(subDays(new Date(), 1)) &&
          asDate(value) <= endOfDay(addDays(new Date(), 7))
        )
      }
    },
  },
]

export const getItemIsClosed = (
  item: Extract<
    ProvidersWorkUnitsPartialUpdateResponse['content_object'],
    { content_type: 'case' }
  >,
) => {
  return item.status_category == 'complete' || item.status_category == 'deferred'
}
